import { createSlice, createEntityAdapter, type PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/rootState';
import type { ClipInstance } from '@/types/clipTypes';

// Entity adapter for clips (uses 'instanceId' as the ID field)
const clipsAdapter = createEntityAdapter<ClipInstance, string>({
  selectId: (clip) => clip.instanceId,
  sortComparer: (a, b) => a.instanceStartTime - b.instanceStartTime,
});

export interface ClipsState extends ReturnType<typeof clipsAdapter.getInitialState> {
  loadingByAssetId: Record<string, boolean>;
  idsByAssetId: Record<string, string[]>;
  idsBySceneId: Record<string, string[]>;
}

const initialState: ClipsState = {
  ...clipsAdapter.getInitialState(),
  loadingByAssetId: {},
  idsByAssetId: {},
  idsBySceneId: {},
};

const slice = createSlice({
  name: 'clips',
  initialState,
  reducers: {
    setSceneClips: (
      state,
      action: PayloadAction<{ sceneInstanceId: string; clips: ClipInstance[] }>
    ) => {
      const { sceneInstanceId, clips } = action.payload;
      clipsAdapter.upsertMany(state, clips);
      state.idsBySceneId[sceneInstanceId] = clips.map((clip) => clip.instanceId);

      // Group by asset
      const byAsset: Record<string, string[]> = {};
      clips.forEach((clip) => {
        if (!byAsset[clip.assetId]) byAsset[clip.assetId] = [];
        byAsset[clip.assetId].push(clip.instanceId);
      });

      // Update asset mappings
      Object.entries(byAsset).forEach(([assetId, ids]) => {
        state.idsByAssetId[assetId] = ids;
        state.loadingByAssetId[assetId] = false;
      });
    },
    setAssetClips: (
      state,
      action: PayloadAction<{ assetInstanceId: string; clips: ClipInstance[] }>
    ) => {
      const { assetInstanceId, clips } = action.payload;
      clipsAdapter.upsertMany(state, clips);
      state.idsByAssetId[assetInstanceId] = clips.map((clip) => clip.instanceId);
      state.loadingByAssetId[assetInstanceId] = false;
    },
    upsertClip: (state, action: PayloadAction<ClipInstance>) => {
      clipsAdapter.upsertOne(state, action.payload);
      const assetId = action.payload.assetId;
      const sceneId = action.payload.sceneInstanceId;
      const clipId = action.payload.instanceId;

      // Update asset mapping
      const assetList = state.idsByAssetId[assetId] || [];
      if (!assetList.includes(clipId)) {
        state.idsByAssetId[assetId] = [...assetList, clipId];
      }

      // Update scene mapping
      const sceneList = state.idsBySceneId[sceneId] || [];
      if (!sceneList.includes(clipId)) {
        state.idsBySceneId[sceneId] = [...sceneList, clipId];
      }
    },
    updateClipVolume: (state, action: PayloadAction<{ clipInstanceId: string; volume: number }>) => {
      const { clipInstanceId, volume } = action.payload;
      const clip = state.entities[clipInstanceId];
      if (clip) {
        clip.instanceVolume = volume;
      } else {
        console.error('❌ [Redux] Clip not found for volume update', { clipId: clipInstanceId });
      }
    },
    // Re-point a clip's reactive-trigger FK on the LIVE entity. Patches only this field (unlike
    // upsertClip, which would replace the whole entity and could revert a concurrent edit).
    updateClipTrigger: (state, action: PayloadAction<{ clipInstanceId: string; triggerAssetInstanceId: string | null }>) => {
      const { clipInstanceId, triggerAssetInstanceId } = action.payload;
      const clip = state.entities[clipInstanceId];
      if (clip) clip.triggerAssetInstanceId = triggerAssetInstanceId;
    },
    removeClip: (state, action: PayloadAction<{ instanceId: string; assetId: string; sceneInstanceId: string }>) => {
      const { instanceId, assetId, sceneInstanceId } = action.payload;
      clipsAdapter.removeOne(state, instanceId);

      // Remove from asset mapping
      const assetList = state.idsByAssetId[assetId] || [];
      state.idsByAssetId[assetId] = assetList.filter((id) => id !== instanceId);

      // Remove from scene mapping
      const sceneList = state.idsBySceneId[sceneInstanceId] || [];
      state.idsBySceneId[sceneInstanceId] = sceneList.filter((id) => id !== instanceId);
    },
    setAssetClipsLoading: (state, action: PayloadAction<{ assetInstanceId: string; loading: boolean }>) => {
      state.loadingByAssetId[action.payload.assetInstanceId] = action.payload.loading;
    },
    clearClipsForAsset: (state, action: PayloadAction<string>) => {
      const assetId = action.payload;
      const ids = state.idsByAssetId[assetId] || [];
      clipsAdapter.removeMany(state, ids);
      delete state.idsByAssetId[assetId];
      delete state.loadingByAssetId[assetId];
    },
    clearClipsForScene: (state, action: PayloadAction<string>) => {
      const sceneId = action.payload;
      const ids = state.idsBySceneId[sceneId] || [];
      clipsAdapter.removeMany(state, ids);

      // Clear all asset mappings for this scene's clips
      ids.forEach((clipId) => {
        const clip = state.entities[clipId];
        if (clip) {
          const assetList = state.idsByAssetId[clip.assetId] || [];
          state.idsByAssetId[clip.assetId] = assetList.filter((id) => id !== clipId);
        }
      });

      delete state.idsBySceneId[sceneId];
    },
  },
});

export const {
  setSceneClips,
  setAssetClips,
  upsertClip,
  updateClipVolume,
  updateClipTrigger,
  removeClip,
  setAssetClipsLoading,
  clearClipsForAsset,
  clearClipsForScene,
} = slice.actions;

export default slice.reducer;

// Selectors
const selectors = clipsAdapter.getSelectors<RootState>((state) => state.clips);

export const selectClipById = (state: RootState, id: string) => selectors.selectById(state, id);

export const selectClipsByAssetId = (state: RootState, assetInstanceId: string): ClipInstance[] => {
  const ids = state.clips.idsByAssetId[assetInstanceId] || [];
  return ids
    .map((id) => selectors.selectById(state, id))
    .filter((clip): clip is ClipInstance => Boolean(clip))
    .sort((a, b) => a.instanceStartTime - b.instanceStartTime);
};

export const reactiveTriggerIdOf = (clip: ClipInstance): string | null =>
  clip.triggerAssetInstanceId ?? null;

export const clipOwnerId = (clip: ClipInstance): string | null =>
  clip.assetId || clip.uiElementId || null;

export const selectReactiveClipsByTrigger = (state: RootState, triggerId: string): ClipInstance[] =>
  selectors.selectAll(state).filter((c) => reactiveTriggerIdOf(c) === triggerId);

export const selectReactiveClipForAsset = (state: RootState, triggerId: string, ownerId: string): ClipInstance | null =>
  selectReactiveClipsByTrigger(state, triggerId).find((c) => clipOwnerId(c) === ownerId) ?? null;

/**
 * Selector factory creating a memoized selector for clips by asset ID.
 * Using a factory pattern is necessary because the selector is used across multiple 
 * component instances. A standard global selector would thrash its cache (size 1) 
 * as components called it with different IDs back-to-back.
 */
export const makeSelectClipsByAssetId = () => createSelector(
  [
    (state: RootState) => state.clips.idsByAssetId,
    (state: RootState) => state.clips.entities,
    (state: RootState, assetInstanceId: string) => assetInstanceId
  ],
  (idsByAssetId, entities, assetInstanceId): ClipInstance[] => {
    const ids = idsByAssetId[assetInstanceId] || [];
    return ids
      .map((id) => entities[id])
      .filter((clip): clip is ClipInstance => Boolean(clip))
      .sort((a, b) => a.instanceStartTime - b.instanceStartTime);
  }
);

export const selectClipsBySceneId = (state: RootState, sceneInstanceId: string): ClipInstance[] => {
  const ids = state.clips.idsBySceneId[sceneInstanceId] || [];
  return ids
    .map((id) => selectors.selectById(state, id))
    .filter((clip): clip is ClipInstance => Boolean(clip))
    .sort((a, b) => a.instanceStartTime - b.instanceStartTime);
};

export const selectClipsLoadingByAssetId = (state: RootState, assetInstanceId: string) =>
  state.clips.loadingByAssetId[assetInstanceId] || false;

